import { useState } from 'react'
import type { WorkoutExerciseHistoryEntry, WorkoutExerciseLog } from '../../api/workoutApi'
import { useTheme } from '../../hooks/useTheme'
import { useTranslation } from '../../i18n'
import { Button, DashedAddButton } from '../ui'
import { groupTitle, type WorkoutGroup } from './workoutGroupHelpers'
import {
  MAX_SET_LOGS,
  type LogActual,
  type LogActuals,
  logActualFromExercise,
} from './workoutLogActuals'

const HISTORY_PREVIEW = 3

type GroupHistorySession = {
  sessionId: number
  sessionStartedAt: string
  blockName: string | null
  exercises: WorkoutExerciseHistoryEntry[]
}

function buildGroupHistorySessions(
  names: string[],
  historyFor: (name: string) => WorkoutExerciseHistoryEntry[],
): GroupHistorySession[] {
  const uniqueNames = [...new Set(names.filter(Boolean))]
  const order = new Map(uniqueNames.map((name, i) => [name, i]))
  const bySession = new Map<number, GroupHistorySession>()

  for (const name of uniqueNames) {
    for (const entry of historyFor(name)) {
      let row = bySession.get(entry.sessionId)
      if (!row) {
        row = {
          sessionId: entry.sessionId,
          sessionStartedAt: entry.sessionStartedAt,
          blockName: entry.blockName,
          exercises: [],
        }
        bySession.set(entry.sessionId, row)
      }
      row.exercises.push(entry)
    }
  }

  for (const row of bySession.values()) {
    row.exercises.sort(
      (a, b) => (order.get(a.exerciseName) ?? 99) - (order.get(b.exerciseName) ?? 99),
    )
  }

  return [...bySession.values()].sort(
    (a, b) => new Date(b.sessionStartedAt).getTime() - new Date(a.sessionStartedAt).getTime(),
  )
}

function ChevronLeftIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

export interface WorkoutSessionActiveViewProps {
  blockName: string | null
  /** Optional title under block name (defaults to workout.sessionTitle) */
  title?: string
  compact?: boolean
  groupIndex: number
  groups: WorkoutGroup[]
  actualsByLogId: LogActuals
  history: WorkoutExerciseHistoryEntry[]
  /** When set (e.g. Pro View groups), history is shown per exercise name. */
  historyByExerciseName?: Record<string, WorkoutExerciseHistoryEntry[]>
  historySessionId?: number | null
  saving?: boolean
  finishing?: boolean
  canceling?: boolean
  finished?: boolean
  error?: string | null
  hideNavHint?: boolean
  onActualChange: (logId: number, next: LogActual) => void
  onPrev: () => void
  onNext: () => void
  onFinish: () => void
  onCancel: () => void
}

export function WorkoutSessionActiveView({
  blockName,
  title,
  compact = false,
  groupIndex,
  groups,
  actualsByLogId,
  history,
  historyByExerciseName,
  historySessionId,
  saving = false,
  finishing = false,
  canceling = false,
  finished = false,
  error = null,
  hideNavHint = false,
  onActualChange,
  onPrev,
  onNext,
  onFinish,
  onCancel,
}: WorkoutSessionActiveViewProps) {
  const theme = useTheme()
  const { t } = useTranslation()
  const accent = theme.linkColor
  const currentGroup = groups[groupIndex]
  const prevGroup = groupIndex > 0 ? groups[groupIndex - 1] : undefined
  const nextGroup = groupIndex < groups.length - 1 ? groups[groupIndex + 1] : undefined

  if (!currentGroup) return null

  const progressPercent = groups.length === 0
    ? 0
    : Math.round(((groupIndex + 1) / groups.length) * 100)
  const progressLabel = t.workout.progress
    .replace('{{current}}', String(groupIndex + 1))
    .replace('{{total}}', String(groups.length))

  const pad = compact ? 'p-4' : 'p-5'
  const titleSize = compact ? 'text-lg' : 'text-xl'
  const isMulti = currentGroup.exercises.length > 1

  function historyForExercise(name: string): WorkoutExerciseHistoryEntry[] {
    const source = historyByExerciseName?.[name]
      ?? (isMulti ? history.filter(e => e.exerciseName === name) : history)
    return historySessionId != null
      ? source.filter(e => e.sessionId !== historySessionId)
      : source
  }
  const note = currentGroup.note?.trim()

  if (compact) {
    return (
      <CompactLiveWorkoutCard
        blockName={blockName}
        progressPercent={progressPercent}
        progressLabel={progressLabel}
        groupIndex={groupIndex}
        groupsLength={groups.length}
        groupTitleText={groupTitle(currentGroup)}
        isMulti={isMulti}
        note={note}
        exercises={currentGroup.exercises}
        actualsByLogId={actualsByLogId}
        history={history}
        historyByExerciseName={historyByExerciseName}
        historySessionId={historySessionId}
        saving={saving}
        finishing={finishing}
        canceling={canceling}
        finished={finished}
        error={error}
        onActualChange={onActualChange}
        onPrev={onPrev}
        onNext={onNext}
        onFinish={onFinish}
        onCancel={onCancel}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <header className="mb-3">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide truncate" style={{ color: theme.text.muted }}>
              {blockName}
            </div>
            <h2 className={`${titleSize} font-bold mt-0.5`} style={{ color: theme.text.primary }}>
              {title ?? t.workout.sessionTitle}
            </h2>
          </div>
          <span
            className="shrink-0 text-sm font-semibold tabular-nums px-3 py-1 rounded-full"
            style={{
              backgroundColor: `${accent}18`,
              color: accent,
              border: `1px solid ${accent}40`,
            }}
          >
            {progressLabel}
          </span>
        </div>
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: theme.inputBgSubtle }}
          role="progressbar"
          aria-valuenow={progressPercent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progressPercent}%`, backgroundColor: accent }}
          />
        </div>
        {!hideNavHint && (
          <p className="text-xs mt-2" style={{ color: theme.text.muted }}>{t.workout.navHint}</p>
        )}
      </header>

      <div className="space-y-3">
        {error && (
          <div
            className="rounded-2xl p-3 text-sm"
            style={{ backgroundColor: 'rgba(239,68,68,0.12)', color: theme.text.primary }}
          >
            {error}
          </div>
        )}

        {prevGroup && (
          <GroupPreviewCard
            label={t.workout.previousExercise}
            group={prevGroup}
            theme={theme}
            variant="previous"
            disabled={saving}
            onSelect={onPrev}
            groupLabel={t.createTrainingBlock.groupLabel}
          />
        )}

        <article
          className={`rounded-3xl ${pad} shadow-sm`}
          style={{
            backgroundColor: theme.cardBgLight,
            border: `1px solid ${theme.cardBorder}`,
            boxShadow: theme.shadow.card,
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <button
              type="button"
              aria-label={t.workout.prev}
              disabled={groupIndex === 0 || saving}
              onClick={onPrev}
              className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center transition-transform active:scale-95 disabled:opacity-40"
              style={{
                backgroundColor: theme.bgColor,
                border: `1px solid ${theme.cardBorder}`,
                color: theme.text.primary,
              }}
            >
              <ChevronLeftIcon />
            </button>
            <h3 className={`flex-1 ${titleSize} font-bold leading-tight text-center px-1`} style={{ color: theme.text.primary }}>
              {groupTitle(currentGroup)}
            </h3>
            <button
              type="button"
              aria-label={t.workout.next}
              disabled={groupIndex >= groups.length - 1 || saving}
              onClick={onNext}
              className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center transition-transform active:scale-95 disabled:opacity-40"
              style={{
                backgroundColor: theme.bgColor,
                border: `1px solid ${theme.cardBorder}`,
                color: theme.text.primary,
              }}
            >
              <ChevronRightIcon />
            </button>
          </div>

          {isMulti && (
            <p
              className="text-[10px] font-bold uppercase tracking-wide text-center mb-2"
              style={{ color: theme.text.muted }}
            >
              {t.createTrainingBlock.groupLabel}
            </p>
          )}

          {note && (
            <p
              className="text-sm text-center mb-4 px-2 leading-snug"
              style={{ color: theme.text.secondary }}
            >
              {note}
            </p>
          )}

          <div className="flex flex-col gap-4 mb-2">
            {currentGroup.exercises.map((ex) => {
              const actual = actualsByLogId[ex.id] ?? logActualFromExercise(ex)
              return (
                <ExerciseActualRow
                  key={ex.id}
                  exercise={ex}
                  showName={isMulti}
                  actual={actual}
                  finished={finished}
                  saving={saving}
                  theme={theme}
                  t={t}
                  onChange={(next) => onActualChange(ex.id, next)}
                />
              )
            })}
          </div>

          <p className="text-xs mb-3" style={{ color: theme.text.muted }}>{t.workout.logOptionalHint}</p>
          {groupIndex >= groups.length - 1 && !finished && (
            <Button
              fullWidth
              size="lg"
              variant="success"
              disabled={finishing || canceling || saving}
              onClick={onFinish}
            >
              {finishing ? t.workout.finishing : t.workout.finish}
            </Button>
          )}
        </article>

        {nextGroup && (
          <GroupPreviewCard
            label={t.workout.nextExercise}
            group={nextGroup}
            theme={theme}
            variant="next"
            disabled={saving}
            onSelect={onNext}
            groupLabel={t.createTrainingBlock.groupLabel}
          />
        )}

        <ExerciseHistoryList
          title={t.workout.historyTitle}
          sessions={buildGroupHistorySessions(
            currentGroup.exercises.map(ex => ex.exerciseName),
            historyForExercise,
          )}
          showExerciseNames={isMulti}
          theme={theme}
          t={t}
        />

        {!finished && (
          <Button
            fullWidth
            size="md"
            variant="danger"
            className="mt-4"
            disabled={finishing || canceling || saving}
            onClick={onCancel}
          >
            {canceling ? t.workout.canceling : t.workout.cancel}
          </Button>
        )}
      </div>
    </div>
  )
}

/** Slim live-workout card for Pro View column (75vw). */
function CompactLiveWorkoutCard({
  blockName,
  progressPercent,
  progressLabel,
  groupIndex,
  groupsLength,
  groupTitleText,
  isMulti,
  note,
  exercises,
  actualsByLogId,
  history,
  historyByExerciseName,
  historySessionId,
  saving,
  finishing,
  canceling,
  finished,
  error,
  onActualChange,
  onPrev,
  onNext,
  onFinish,
  onCancel,
}: {
  blockName: string | null
  progressPercent: number
  progressLabel: string
  groupIndex: number
  groupsLength: number
  groupTitleText: string
  isMulti: boolean
  note?: string
  exercises: WorkoutExerciseLog[]
  actualsByLogId: LogActuals
  history: WorkoutExerciseHistoryEntry[]
  historyByExerciseName?: Record<string, WorkoutExerciseHistoryEntry[]>
  historySessionId?: number | null
  saving: boolean
  finishing: boolean
  canceling: boolean
  finished: boolean
  error: string | null
  onActualChange: (logId: number, next: LogActual) => void
  onPrev: () => void
  onNext: () => void
  onFinish: () => void
  onCancel: () => void
}) {
  const theme = useTheme()
  const { t } = useTranslation()
  const accent = theme.linkColor
  function historyForExercise(name: string): WorkoutExerciseHistoryEntry[] {
    const source = historyByExerciseName?.[name]
      ?? (isMulti ? history.filter(e => e.exerciseName === name) : history)
    return historySessionId != null
      ? source.filter(e => e.sessionId !== historySessionId)
      : source
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3 px-0.5">
        <div
          className="flex-1 h-1.5 rounded-full overflow-hidden"
          style={{ backgroundColor: theme.inputBgSubtle }}
          role="progressbar"
          aria-valuenow={progressPercent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progressPercent}%`, backgroundColor: accent }}
          />
        </div>
        <span className="text-xs font-semibold tabular-nums shrink-0" style={{ color: accent }}>
          {progressLabel}
        </span>
      </div>

      {error && (
        <div
          className="rounded-2xl px-3 py-2.5 text-xs"
          style={{ backgroundColor: 'rgba(239,68,68,0.12)', color: theme.text.primary }}
        >
          {error}
        </div>
      )}

      <article
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: theme.cardBgLight,
          border: `1px solid ${theme.cardBorder}`,
          boxShadow: theme.shadow.card,
        }}
      >
        <div className="flex items-center gap-2 px-3 pt-3 pb-2">
          <button
            type="button"
            aria-label={t.workout.prev}
            disabled={groupIndex === 0 || saving}
            onClick={onPrev}
            className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-transform active:scale-95 disabled:opacity-35"
            style={{
              backgroundColor: theme.bgColor,
              border: `1px solid ${theme.cardBorder}`,
              color: theme.text.primary,
            }}
          >
            <ChevronLeftIcon />
          </button>
          <div className="flex-1 min-w-0 text-center px-1">
            {blockName && (
              <p className="text-[10px] font-semibold uppercase tracking-wide truncate mb-0.5" style={{ color: theme.text.muted }}>
                {blockName}
              </p>
            )}
            {isMulti && (
              <p className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color: accent }}>
                {t.createTrainingBlock.groupLabel}
              </p>
            )}
            <h3 className="text-base font-bold leading-snug" style={{ color: theme.text.primary }}>
              {groupTitleText}
            </h3>
          </div>
          <button
            type="button"
            aria-label={t.workout.next}
            disabled={groupIndex >= groupsLength - 1 || saving}
            onClick={onNext}
            className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-transform active:scale-95 disabled:opacity-35"
            style={{
              backgroundColor: theme.bgColor,
              border: `1px solid ${theme.cardBorder}`,
              color: theme.text.primary,
            }}
          >
            <ChevronRightIcon />
          </button>
        </div>

        {note && (
          <div
            className="mx-3 mb-3 rounded-xl px-3 py-2.5"
            style={{
              backgroundColor: `${theme.buttonColor}12`,
              border: `1px solid ${theme.buttonColor}22`,
            }}
          >
            <p className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: accent }}>
              {t.workout.blockNoteLabel}
            </p>
            <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: theme.text.secondary }}>
              {note}
            </p>
          </div>
        )}

        <div className="px-3 pb-3 flex flex-col gap-3">
          {exercises.map((ex) => {
            const actual = actualsByLogId[ex.id] ?? logActualFromExercise(ex)
            return (
              <ExerciseActualRow
                key={ex.id}
                exercise={ex}
                showName={isMulti}
                actual={actual}
                finished={finished}
                saving={saving}
                compact
                theme={theme}
                t={t}
                onChange={(next) => onActualChange(ex.id, next)}
              />
            )
          })}
        </div>

        {!finished && groupIndex >= groupsLength - 1 && (
          <div className="px-3 pb-3">
            <Button
              fullWidth
              size="md"
              variant="success"
              disabled={finishing || canceling || saving}
              onClick={onFinish}
            >
              {finishing ? t.workout.finishing : t.workout.finish}
            </Button>
          </div>
        )}
      </article>

      <ExerciseHistoryList
        title={t.workout.historyTitle}
        sessions={buildGroupHistorySessions(
          exercises.map(ex => ex.exerciseName),
          historyForExercise,
        )}
        showExerciseNames={isMulti}
        compact
        theme={theme}
        t={t}
      />

      {!finished && (
        <Button
          fullWidth
          size="sm"
          variant="danger"
          disabled={finishing || canceling || saving}
          onClick={onCancel}
        >
          {canceling ? t.workout.canceling : t.workout.cancel}
        </Button>
      )}
    </div>
  )
}

function ExerciseHistoryList({
  title,
  sessions,
  showExerciseNames = false,
  compact = false,
  theme,
  t,
}: {
  title: string
  sessions: GroupHistorySession[]
  showExerciseNames?: boolean
  compact?: boolean
  theme: ReturnType<typeof useTheme>
  t: ReturnType<typeof useTranslation>['t']
}) {
  const [expanded, setExpanded] = useState(false)
  const extraCount = Math.max(0, sessions.length - HISTORY_PREVIEW)
  const visible = expanded ? sessions : sessions.slice(0, HISTORY_PREVIEW)
  const cardPad = compact ? 'px-3 py-2.5 rounded-xl' : 'px-4 py-3 rounded-2xl'
  const dateSize = compact ? 'text-[11px]' : 'text-xs'

  if (sessions.length === 0) return null

  return (
    <section>
      <h3 className={`${compact ? 'text-xs' : 'text-sm'} font-bold mb-1`} style={{ color: theme.text.primary }}>
        {title}
      </h3>
      <p className={`${compact ? 'text-[11px]' : 'text-xs'} mb-2`} style={{ color: theme.text.muted }}>
        {t.workout.historySetsHint}
      </p>
      <div className="flex flex-col gap-2">
        {visible.map((session) => (
          <div
            key={session.sessionId}
            className={cardPad}
            style={{
              backgroundColor: compact ? theme.bgColor : theme.inputBgSubtle,
              border: `1px solid ${theme.cardBorder}`,
            }}
          >
            <div className="flex justify-between gap-2 mb-2">
              <span className={`${dateSize} font-medium`} style={{ color: theme.text.muted }}>
                {formatHistoryDate(session.sessionStartedAt)}
              </span>
              <span className={`${dateSize} truncate max-w-[45%] text-right`} style={{ color: theme.text.muted }}>
                {session.blockName}
              </span>
            </div>
            <div className={showExerciseNames ? 'flex flex-col gap-3' : undefined}>
              {session.exercises.map((entry) => (
                <div key={entry.logId}>
                  {showExerciseNames && (
                    <p
                      className={`${compact ? 'text-xs' : 'text-sm'} font-bold mb-1`}
                      style={{ color: theme.text.primary }}
                    >
                      {entry.exerciseName}
                    </p>
                  )}
                  <HistoryMetrics entry={entry} theme={theme} t={t} hideSetsLabel={showExerciseNames} />
                </div>
              ))}
            </div>
          </div>
        ))}
        {extraCount > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="w-full min-h-[44px] rounded-2xl text-sm font-semibold transition-transform active:scale-[0.99]"
            style={{
              color: theme.accent.text,
              backgroundColor: `${theme.buttonColor}12`,
            }}
          >
            {expanded
              ? t.workout.historyShowLess
              : t.workout.historyShowMore.replace('{{count}}', String(extraCount))}
          </button>
        )}
      </div>
    </section>
  )
}

function setIsLogged(set: { weight?: string | null; reps?: string | null }): boolean {
  return Boolean((set.weight ?? '').trim() || (set.reps ?? '').trim())
}

function ExerciseActualRow({
  exercise,
  showName,
  actual,
  finished,
  saving,
  compact = false,
  theme,
  t,
  onChange,
}: {
  exercise: WorkoutExerciseLog
  showName: boolean
  actual: LogActual
  finished: boolean
  saving: boolean
  compact?: boolean
  theme: ReturnType<typeof useTheme>
  t: ReturnType<typeof useTranslation>['t']
  onChange: (next: LogActual) => void
}) {
  const plannedHint = formatPlannedSummary(exercise)
  const setLogs = actual.setLogs ?? []
  const canAddSet = setLogs.length < MAX_SET_LOGS && !finished && !saving
  const accent = theme.linkColor
  const fieldPad = compact ? 'px-2 py-2.5 text-base' : 'px-3 py-3 text-lg'

  function patch(partial: Partial<LogActual>) {
    onChange({ ...actual, ...partial })
  }

  function updateSet(index: number, field: 'weight' | 'reps', value: string) {
    const next = setLogs.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    patch({ setLogs: next })
  }

  function addSet() {
    if (!canAddSet) return
    patch({
      setLogs: [
        ...setLogs,
        { weight: '', reps: '' },
      ],
    })
  }

  function removeSet(index: number) {
    const next = setLogs.filter((_, i) => i !== index)
    patch({ setLogs: next.length > 0 ? next : [{ weight: '', reps: '' }] })
  }

  return (
    <div className={compact ? 'rounded-2xl p-3' : 'rounded-2xl p-4'} style={{ backgroundColor: theme.inputBgSubtle }}>
      {showName && (
        <p className={`${compact ? 'text-sm' : 'text-base'} font-bold mb-3`} style={{ color: theme.text.primary }}>
          {exercise.exerciseName}
        </p>
      )}

      {plannedHint && (
        <div
          className="inline-flex items-baseline gap-2 rounded-full px-3 py-1.5 mb-4"
          style={{
            backgroundColor: `${accent}14`,
            border: `1px solid ${accent}28`,
          }}
        >
          <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: accent }}>
            {t.workout.targetLabel}
          </span>
          <span className="text-sm font-bold tabular-nums" style={{ color: theme.text.primary }}>
            {plannedHint}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {setLogs.map((set, index) => {
          const logged = setIsLogged(set)
          return (
            <div key={index} className="flex items-start gap-2">
              <div
                className={`shrink-0 ${compact ? 'w-9 h-9 mt-1' : 'w-11 h-11 mt-0.5'} rounded-full flex items-center justify-center text-sm font-bold tabular-nums transition-colors`}
                style={{
                  backgroundColor: logged ? accent : theme.cardBgLight,
                  color: logged ? theme.buttonTextColor : theme.text.muted,
                  boxShadow: logged ? `0 4px 12px ${accent}40` : undefined,
                }}
                aria-hidden
              >
                {index + 1}
              </div>

              <div className="flex-1 min-w-0 grid grid-cols-2 gap-2">
                <label className="min-w-0">
                  <input
                    value={set.weight ?? ''}
                    disabled={finished || saving}
                    onChange={(e) => updateSet(index, 'weight', e.target.value)}
                    className={`w-full rounded-2xl ${fieldPad} font-bold tabular-nums text-center outline-none transition-colors`}
                    style={{
                      backgroundColor: theme.cardBgLight,
                      color: theme.text.primary,
                      border: `1px solid ${logged ? `${accent}55` : theme.cardBorder}`,
                      minHeight: compact ? 44 : 48,
                    }}
                    placeholder={exercise.plannedWeight || '—'}
                    inputMode="decimal"
                    aria-label={t.workout.setWeightLabel.replace('{n}', String(index + 1))}
                  />
                  <span className="block mt-1 text-[11px] font-medium text-center" style={{ color: theme.text.muted }}>
                    {t.workout.setWeight}
                  </span>
                </label>
                <label className="min-w-0">
                  <input
                    value={set.reps ?? ''}
                    disabled={finished || saving}
                    onChange={(e) => updateSet(index, 'reps', e.target.value)}
                    className={`w-full rounded-2xl ${fieldPad} font-bold tabular-nums text-center outline-none transition-colors`}
                    style={{
                      backgroundColor: theme.cardBgLight,
                      color: theme.text.primary,
                      border: `1px solid ${logged ? `${accent}55` : theme.cardBorder}`,
                      minHeight: compact ? 44 : 48,
                    }}
                    placeholder={exercise.plannedReps != null ? String(exercise.plannedReps) : '—'}
                    inputMode="numeric"
                    aria-label={t.workout.setRepsLabel.replace('{n}', String(index + 1))}
                  />
                  <span className="block mt-1 text-[11px] font-medium text-center" style={{ color: theme.text.muted }}>
                    {t.workout.setReps}
                  </span>
                </label>
              </div>

              {!finished ? (
                <button
                  type="button"
                  disabled={saving || setLogs.length <= 1}
                  onClick={() => removeSet(index)}
                  className={`shrink-0 ${compact ? 'w-10 h-10 mt-0.5' : 'w-11 h-11 mt-0.5'} rounded-2xl flex items-center justify-center transition-transform active:scale-95 disabled:opacity-25`}
                  style={{ color: theme.text.muted }}
                  aria-label={t.workout.removeSet}
                >
                  <CloseIcon />
                </button>
              ) : (
                <span className={compact ? 'w-10' : 'w-11'} />
              )}
            </div>
          )
        })}

        {canAddSet && (
          <DashedAddButton onClick={addSet} className={compact ? '!rounded-xl !p-3 !text-xs' : ''}>
            {t.workout.addSet}
          </DashedAddButton>
        )}
      </div>

      <label className="block mt-4">
        <span className="text-[11px] font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: theme.text.muted }}>
          {t.workout.commentLabel}
        </span>
        <textarea
          value={actual.comment}
          disabled={finished || saving}
          onChange={(e) => patch({ comment: e.target.value })}
          rows={compact ? 2 : 2}
          className="w-full rounded-2xl px-4 py-3 text-sm outline-none resize-none leading-relaxed"
          style={{
            backgroundColor: theme.cardBgLight,
            color: theme.text.primary,
            border: `1px solid ${theme.cardBorder}`,
            minHeight: 48,
          }}
          placeholder={t.workout.commentPlaceholder}
        />
      </label>
    </div>
  )
}

function GroupPreviewCard({
  label,
  group,
  theme,
  variant,
  disabled,
  onSelect,
  groupLabel,
}: {
  label: string
  group: WorkoutGroup
  theme: ReturnType<typeof useTheme>
  variant: 'previous' | 'next'
  disabled?: boolean
  onSelect: () => void
  groupLabel: string
}) {
  const title = groupTitle(group)
  const note = group.note?.trim()
  const isGroup = group.exercises.length > 1

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className="w-full text-left rounded-2xl px-4 py-3 transition-all active:scale-[0.99] disabled:opacity-50"
      style={{
        backgroundColor: theme.brand.light,
        border: `1px solid ${theme.accent.border}`,
      }}
    >
        <span className="text-[10px] font-bold uppercase tracking-wide mb-1 block" style={{ color: theme.accent.text }}>
          {label}
        </span>
      {isGroup && (
        <span className="text-[10px] font-bold uppercase tracking-wide block mb-0.5" style={{ color: theme.text.muted }}>
          {groupLabel}
        </span>
      )}
      <span
        className={`font-semibold truncate block ${variant === 'previous' ? 'text-sm' : 'text-base'}`}
        style={{ color: theme.text.primary }}
      >
        {title}
      </span>
      {note && (
        <span className="text-xs line-clamp-2 mt-0.5 block" style={{ color: theme.text.muted }}>
          {note}
        </span>
      )}
    </button>
  )
}

function formatPlannedSummary(exercise: WorkoutExerciseLog): string | null {
  const parts: string[] = []
  if (exercise.plannedSets != null && exercise.plannedSets > 0) {
    parts.push(String(exercise.plannedSets))
  }
  if (exercise.plannedWeight) parts.push(exercise.plannedWeight)
  if (exercise.plannedReps != null) parts.push(String(exercise.plannedReps))
  return parts.length > 0 ? parts.join(' × ') : null
}

function HistoryMetrics({
  entry,
  theme,
  t,
  hideSetsLabel = false,
}: {
  entry: WorkoutExerciseHistoryEntry
  theme: ReturnType<typeof useTheme>
  t: ReturnType<typeof useTranslation>['t']
  hideSetsLabel?: boolean
}) {
  const sets = entry.setLogs ?? []
  const hasLoggedSet = sets.some((s) => (s.weight ?? '').trim() || (s.reps ?? '').trim())
  const comment = (entry.comment ?? '').trim()

  if (!hasLoggedSet && !comment) {
    return (
      <p className="text-sm" style={{ color: theme.text.muted }}>
        {t.workout.historyNoActuals}
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {hasLoggedSet && (
        <div>
          {!hideSetsLabel && (
            <div className="text-[10px] font-semibold uppercase tracking-wide mb-1" style={{ color: theme.text.muted }}>
              {t.workout.historySets}
            </div>
          )}
          <div className="flex flex-col gap-1">
            {sets.map((set, index) => {
              const weight = (set.weight ?? '').trim()
              const reps = (set.reps ?? '').trim()
              const line = weight && reps
                ? `${weight} × ${reps}`
                : weight || (reps ? `${reps} ${t.workout.repsSuffix}` : '—')
              return (
                <div key={index} className="flex gap-2 text-sm">
                  <span className="tabular-nums font-medium" style={{ color: theme.text.muted }}>
                    {index + 1}.
                  </span>
                  <span
                    className="font-semibold tabular-nums"
                    style={{ color: weight || reps ? theme.text.primary : theme.text.muted }}
                  >
                    {line}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
      {comment && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide mb-0.5" style={{ color: theme.text.muted }}>
            {t.workout.historyComment}
          </div>
          <p className="text-sm leading-snug whitespace-pre-wrap" style={{ color: theme.text.secondary }}>
            {comment}
          </p>
        </div>
      )}
    </div>
  )
}

function formatHistoryDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}
